import * as React from "react";
import { createPortal } from "react-dom";
import { Icon, IconInput } from "./Icon";

/**
 * Ported from ScadsysIdentity's components/Modal.tsx, with the accessibility gaps closed.
 *
 * The reference renders inline (so a modal inside an `overflow-hidden` panel can be clipped), has no
 * Escape handler, and never restores focus to the control that opened it. Here it portals to
 * document.body, closes on Escape, and returns focus on unmount. Everything else — the size scale,
 * the icon header, the body-scroll lock — is the same, so call sites port across unchanged.
 */

export type ModalSize = "sm" | "md" | "lg" | "xl" | "full";

const SIZES: Record<ModalSize, string> = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
    full: "max-w-[90vw]",
};

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    icon?: IconInput;
    iconColor?: string;
    size?: ModalSize;
    children: React.ReactNode;
    footer?: React.ReactNode;
    closeOnOverlayClick?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    icon,
    iconColor = "text-primary",
    size = "md",
    children,
    footer,
    closeOnOverlayClick = true,
}) => {
    const titleId = React.useId();

    React.useEffect(() => {
        if (!isOpen) return;

        // Remember who opened us so focus can go back there when we close; otherwise focus lands on
        // <body> and keyboard users lose their place in the list they were acting on.
        const opener = document.activeElement as HTMLElement | null;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", onKeyDown);
            opener?.focus?.();
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && closeOnOverlayClick) onClose();
    };

    return createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby={title ? titleId : undefined}>
            <div className="fixed inset-0 bg-neutral-900/75 transition-opacity" aria-hidden="true" onClick={handleOverlayClick} />

            <div className="flex min-h-screen items-center justify-center p-4">
                <div className={`relative z-10 w-full ${SIZES[size]} overflow-hidden rounded-card bg-surface text-left shadow-xl`}>
                    {title && (
                        <div className="flex items-center justify-between border-b border-border bg-surface-muted px-6 py-4">
                            <div className="flex items-center gap-2">
                                {icon && <Icon icon={icon} className={`h-5 w-5 ${iconColor}`} />}
                                <h3 className="text-lg font-semibold text-text-strong" id={titleId}>
                                    {title}
                                </h3>
                            </div>
                            <button
                                type="button"
                                className="rounded-sm p-1 text-text-weak transition-colors hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                onClick={onClose}
                            >
                                <span className="sr-only">Close</span>
                                <Icon icon={["fad", "xmark"]} className="h-5 w-5" />
                            </button>
                        </div>
                    )}

                    <div className="bg-surface">{children}</div>

                    {footer && <div className="flex justify-end gap-3 border-t border-border bg-surface-muted px-6 py-4">{footer}</div>}
                </div>
            </div>
        </div>,
        document.body
    );
};

export const ModalBody: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "p-6" }) => (
    <div className={className}>{children}</div>
);

export const ModalFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => (
    <div className={`flex justify-end gap-3 ${className}`}>{children}</div>
);

export default Modal;
